import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Button from 'flarum/common/components/Button';

/**
 * A horizontal pill-row nav rendered at the top of the index content. The items
 * come from Flarum's canonical IndexSidebar.navItems ItemList — so anything an
 * installed extension contributes shows up automatically. (Mirrors the proven
 * MosaicHeroNav / avocado pattern — far more robust than relocating DOM.)
 *
 * Per spec: individual tag links are dropped (they pollute the bar); we keep
 * "All Discussions" and the top-level "Tags" link that goes to the tags page.
 */
/** Start a discussion the way the sidebar's button does (a log-in prompt for guests). */
export function startDiscussion() {
  try {
    const sidebar = new IndexSidebar();
    sidebar.attrs = {};
    sidebar.newDiscussionAction().catch(() => {});
  } catch (e) {
    /* composer unavailable */
  }
}
export default class BespokeHeaderNav extends Component {
  /**
   * Collect the nav data without rendering: the filtered nav-item vnodes plus
   * the New Discussion button. Extracted so companion themes can override
   * `view()` with their own DOM (e.g. a bento grid) while reusing the exact
   * same item pipeline — anything an installed extension contributes to
   * IndexSidebar.navItems keeps showing up.
   */
  navData() {
    let sidebar;
    try {
      sidebar = new IndexSidebar();
      sidebar.attrs = {};
    } catch (e) {
      return null;
    }
    let navList;
    try {
      navList = sidebar.navItems();
    } catch (e) {
      return null;
    }
    ['moreTags', 'separator'].forEach(k => navList.remove(k));
    const pills = navList.toArray().filter(vnode => {
      if (!vnode || typeof vnode.tag === 'string') return false;
      const attrs = vnode.attrs || {};
      if ('model' in attrs) return false; // per-tag entries carry a `model`
      // Only real nav ACTIONS (links/buttons). Some extensions park entire
      // widgets in navItems (e.g. OnAir's Live Now panel) — rendering those as
      // a pill/tile produces a mangled card, so drop anything not actionable.
      if (!attrs.href && typeof attrs.onclick !== 'function') return false;
      const href = String(attrs.href || '');
      if (/\/t\//.test(href)) return false; // individual tag links: /t/<slug>
      return true;
    });
    let newDiscussion = null;
    try {
      const canStart = app.forum.attribute('canStartDiscussion') || !app.session.user;
      newDiscussion = m(Button, {
        icon: "fas fa-edit",
        className: "Button Button--primary IndexPage-newDiscussion",
        disabled: !canStart,
        onclick: () => startDiscussion()
      }, app.translator.trans('core.forum.index.start_discussion_button'));
    } catch (e) {
      /* no new-discussion button */
    }

    // Also handed out on its own (it stays in `pills` too, for themes that
    // draw those): the bar keeps it when the Navigation widget takes the rest.
    const markRead = pills.find(vnode => vnode.itemName === 'bespoke-markAllRead') || null;
    if (!pills.length && !newDiscussion) return null;
    return {
      pills,
      newDiscussion,
      markRead
    };
  }
  view() {
    const data = this.navData();
    if (!data) return null;

    /*
     * 🚨 Mark all as read stays in the bar when the Navigation widget takes the
     * pills. The widget is "placed" once it is anywhere in the layout, including
     * a zone set to show on other pages only, and placing it also hides Flarum's
     * sidebar nav on desktop (body:has(.BespokeHeaderNav)), so the action went
     * with it and the index had no copy at all. The bar owns it; the widget
     * leaves it out, so it never shows twice.
     */
    const markRead = this.attrs.markRead !== false;
    const list = this.attrs.pills !== false ? data.pills.filter(v => markRead || v !== data.markRead) : markRead && data.markRead ? [data.markRead] : [];
    const pills = list.length ? list : null;
    const button = this.attrs.button !== false ? data.newDiscussion : null;
    if (!pills && !button) {
      // Both moved into widgets: keep an empty, hidden bar. It marks this as
      // the discussions index for relocateAuxNav(), which runs before the
      // widget zones are built and, finding no bar at all, took the index for
      // another page and copied Flarum's whole sidebar nav into the content
      // (Subarist, 2026-10-15).
      return m("nav", {
        className: "BespokeHeaderNav BespokeHeaderNav--moved",
        "aria-hidden": "true"
      });
    }
    return m("nav", {
      className: "BespokeHeaderNav",
      "aria-label": extractText(app.translator.trans('ernestdefoe-bespoke.forum.header_nav_label'))
    }, button, pills && m("div", {
      className: "BespokeHeaderNav-pills"
    }, pills));
  }
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/components/BespokeHeaderNav', BespokeHeaderNav);